import { SyncOutlined } from '@ant-design/icons';
import { KakaoBrandMessageDoc } from '@gooduncles/gu-app-schema';
import { Button, message } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';

import { RangeEventValue, dayjsesToDateWhere, getDefaultDates } from 'src/utils/date-util';
import { callSyncKakaoBrandMessage } from 'src/utils/firebase/firebase-callable';
import { observeKakaoBrandMessage } from 'src/utils/firebase/firebase-short-cut';

import { useTitle } from 'src/hooks/useTitle';

import TableOptions from '../../Common/TableOptions/TableOptions';
import KakaoBrandMessageResultTable from './KakaoBrandMessageResultTable';

const defaultValue = getDefaultDates(1, 1);

const KakaoBrandMessageResult: FC = () => {
  useTitle('GU 관리자 | 브랜드 메시지 결과');
  const [rowData, setRowData] = useState<KakaoBrandMessageDoc[]>([]);
  const [dates, setDates] = useState<RangeEventValue>(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [leftDate, rightDate] = useMemo(() => {
    return dates?.[0] && dates?.[1]
      ? dayjsesToDateWhere(dates[0], dates[1])
      : dayjsesToDateWhere(defaultValue[0], defaultValue[1]);
  }, [dates]);

  useEffect(() => {
    const subscription = observeKakaoBrandMessage([], {
      sortKey: '_timeCreate',
      orderBy: 'desc',
      startValue: leftDate,
      endValue: rightDate,
    }).subscribe((docs) => {
      setRowData(docs);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [leftDate, rightDate]);

  const handleSyncResults = async () => {
    if (selectedIds.length === 0) return;
    setSyncing(true);
    try {
      const response = await callSyncKakaoBrandMessage({ docIds: selectedIds });
      if (response.data.result === 'success') {
        const { stats } = response.data;
        message.success(`동기화 완료: ${stats?.synced ?? 0}건 성공, ${stats?.failed ?? 0}건 실패`);
      } else {
        message.error(`동기화 실패: ${response.data.reason}`);
      }
    } catch (error) {
      message.error('동기화 중 오류가 발생했습니다.');
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className='height100 flexColumn'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TableOptions setDates={setDates} defaultValue={defaultValue} />
        <Button
          type='primary'
          icon={<SyncOutlined spin={syncing} />}
          loading={syncing}
          onClick={handleSyncResults}
          disabled={selectedIds.length === 0}>
          발송결과 갱신 {selectedIds.length > 0 && `(${selectedIds.length}건)`}
        </Button>
      </div>
      <KakaoBrandMessageResultTable rowData={rowData} onSelectionChanged={setSelectedIds} />
    </div>
  );
};

export default KakaoBrandMessageResult;
